import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { trainingApi } from "../../services/trainingApi";
import { usePermissions } from "../../hooks/usePermissions";
import { PageHelpBanner } from "../../components/PageHelpBanner";
import { ReasonDialog } from "../../components/ReasonDialog";
import { formatApiErrorDetail } from "../../lib/api";
import { useLocalization } from "../../hooks/useLocalization";

export default function TrainingPracticalReviewsPage() {
  const { t } = useLocalization();
  const { canViewTraining, canAssignTraining } = usePermissions();
  const [assignments, setAssignments] = useState([]);
  const [courses, setCourses] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [certDefs, setCertDefs] = useState([]);
  const [evalForm, setEvalForm] = useState({ employee_id: "", certification_key: "", passed: true, notes: "" });
  const [approvingId, setApprovingId] = useState(null);

  const load = () => {
    trainingApi.listAssignments().then((r) => setAssignments(r.data.filter((a) => a.status === "PRACTICAL_REVIEW_REQUIRED"))).catch(() => {});
    trainingApi.listCourses().then((r) => setCourses(r.data)).catch(() => {});
    trainingApi.listEmployeesForAssignment().then((r) => setEmployees(r.data)).catch(() => {});
    trainingApi.listCertificationDefs().then((r) => setCertDefs(r.data)).catch(() => {});
  };

  useEffect(() => { if (canViewTraining) load(); }, [canViewTraining]);

  if (!canViewTraining) return <p className="text-sm text-gray-500" data-testid="practical-reviews-access-denied">{t("common.accessDenied")}</p>;

  const employeeName = (id) => employees.find((e) => e.id === id)?.legal_name || id;
  const courseName = (id) => courses.find((c) => c.id === id)?.title_en || id;

  const approvingAssignment = assignments.find((a) => a.id === approvingId);

  const approveAssignment = async (id, notes) => {
    try { await trainingApi.approveAssignment(id, notes || undefined); toast.success(t("mgmtPracticalReviews.reviewApproved")); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); throw e; }
  };

  const recordEvaluation = async () => {
    if (!evalForm.employee_id || !evalForm.certification_key) { toast.error(t("mgmtPracticalReviews.chooseEmployeeAndCert")); return; }
    try {
      await trainingApi.recordEvaluation({ ...evalForm, checklist: [], evidence_urls: [] });
      toast.success(evalForm.passed ? t("mgmtPracticalReviews.certificationIssued") : t("mgmtPracticalReviews.markedForRetraining"));
      setEvalForm({ employee_id: "", certification_key: "", passed: true, notes: "" });
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-5" data-testid="practical-reviews-page">
      <PageHelpBanner pageKey="training-practical-reviews" title={t("pageHelp.defaultTitle")}>
        <p>{t("mgmtPracticalReviews.help")}</p>
      </PageHelpBanner>
      <div>
        <h2 className="text-2xl font-bold tracking-tight text-gray-900">{t("mgmtPracticalReviews.title")}</h2>
        <p className="text-sm text-gray-500">{t("mgmtPracticalReviews.subtitle")}</p>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 overflow-x-auto">
        <p className="px-4 py-3 text-xs uppercase tracking-[0.2em] text-gray-500 border-b border-gray-100">{t("mgmtPracticalReviews.pendingTableTitle")}</p>
        <table className="w-full text-sm min-w-[640px]">
          <thead className="bg-gray-50 text-gray-500 text-left"><tr><th className="px-4 py-3 font-medium">{t("mgmtPayroll.colEmployee")}</th><th className="px-4 py-3 font-medium">{t("mgmtPracticalReviews.course")}</th><th className="px-4 py-3 font-medium"></th></tr></thead>
          <tbody>
            {assignments.map((a) => (
              <tr key={a.id} className="border-t border-gray-100" data-testid={`practical-review-row-${a.id}`}>
                <td className="px-4 py-2 font-semibold text-gray-800">{employeeName(a.employee_id)}</td>
                <td className="px-4 py-2 text-gray-600">{courseName(a.course_id)}</td>
                <td className="px-4 py-2">
                  {canAssignTraining && <button data-testid={`approve-practical-btn-${a.id}`} onClick={() => setApprovingId(a.id)} className="px-2 py-1 rounded text-xs font-semibold bg-green-600 text-white hover:bg-green-700">{t("common.approve")}</button>}
                </td>
              </tr>
            ))}
            {assignments.length === 0 && <tr><td colSpan={3} className="px-4 py-6 text-center text-gray-500" data-testid="no-practical-reviews-row">{t("mgmtPracticalReviews.noReviewsPending")}</td></tr>}
          </tbody>
        </table>
      </div>

      {canAssignTraining && (
        <div className="bg-white border border-gray-200 rounded-xl p-4 space-y-3" data-testid="record-evaluation-panel">
          <p className="text-xs uppercase tracking-[0.2em] text-gray-500">{t("mgmtPracticalReviews.recordEvaluationTitle")}</p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <select data-testid="eval-employee-select" value={evalForm.employee_id} onChange={(e) => setEvalForm({ ...evalForm, employee_id: e.target.value })} className="border border-gray-200 rounded-lg px-3 py-1.5 text-sm">
              <option value="">{t("mgmtTrainingAssignments.employeeEllipsis")}</option>
              {employees.map((e) => <option key={e.id} value={e.id}>{e.legal_name}</option>)}
            </select>
            <select data-testid="eval-cert-select" value={evalForm.certification_key} onChange={(e) => setEvalForm({ ...evalForm, certification_key: e.target.value })} className="border border-gray-200 rounded-lg px-3 py-1.5 text-sm">
              <option value="">{t("mgmtPracticalReviews.certificationEllipsis")}</option>
              {certDefs.map((c) => <option key={c.key} value={c.key}>{c.title_en}</option>)}
            </select>
            <select data-testid="eval-passed-select" value={evalForm.passed ? "true" : "false"} onChange={(e) => setEvalForm({ ...evalForm, passed: e.target.value === "true" })} className="border border-gray-200 rounded-lg px-3 py-1.5 text-sm">
              <option value="true">{t("mgmtPracticalReviews.passed")}</option>
              <option value="false">{t("mgmtPracticalReviews.failedNeedsRetraining")}</option>
            </select>
            <button data-testid="record-evaluation-btn" onClick={recordEvaluation} className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-blue-600 text-white hover:bg-blue-700">{t("mgmtPracticalReviews.recordEvaluation")}</button>
          </div>
          {certDefs.length === 0 && <p className="text-xs text-gray-500" data-testid="no-cert-defs-notice">{t("mgmtPracticalReviews.noCertDefsNotice")}</p>}
        </div>
      )}

      {approvingAssignment && (
        <ReasonDialog
          open onOpenChange={(v) => !v && setApprovingId(null)} title={t("mgmtPracticalReviews.approvePracticalReview")} required={false}
          contextItems={[
            { label: t("mgmtPayroll.employeeName"), value: employeeName(approvingAssignment.employee_id) },
            { label: t("mgmtPracticalReviews.course"), value: courseName(approvingAssignment.course_id) },
          ]}
          reasonLabel={t("mgmtPracticalReviews.notesOptional")} reasonPlaceholder={t("mgmtPracticalReviews.observationsPlaceholder")}
          confirmLabel={t("common.approve")} onConfirm={(notes) => approveAssignment(approvingAssignment.id, notes)}
          testId="approve-practical-dialog"
        />
      )}
    </div>
  );
}
